// One order's commission (Finance): can it be paid yet and why not, costs
// finalized, margins, the Client Rep / Order Rep split, a review decision when
// the plans don't say which pool applies, adjustments and the full history.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, CircleAlert } from "lucide-react";
import { api } from "@/lib/api";
import {
  ADJ_SOURCE_LABEL,
  LINE_STATUS_LABEL,
  type OrderDetail,
  SPLIT_PRESET_ORDER,
  type SplitPreset,
  errReason,
  eventDetail,
  eventLabel,
  fmtDay,
  lineStatusTone,
  money,
  nameOr,
  ratePct,
  reviewText,
  roleLabel,
  usePlanStatus,
  usePlans,
} from "@/lib/compPlans";
import { Button } from "@/components/ui/Button";
import { Field, INPUT, Loading, Notice, Pill, Section, TEXTAREA, T_HEAD, T_NUM, T_ROW, T_TD, T_TH, T_WRAP } from "./parts";

export function OrderCommissionDetail({ orderId }: { orderId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["commissions", "order", orderId],
    queryFn: () => api<OrderDetail>(`/commissions/orders/${orderId}`),
  });
  const [msg, setMsg] = useState<{ tone: "ok" | "risk"; text: string } | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["commissions"] });
  };

  const finalize = useMutation({
    mutationFn: () => api<{ ok: boolean; earnedCommissionIds: string[] }>(`/orders/${orderId}/finalize-costs`, { method: "POST" }),
    onSuccess: (r) => {
      setMsg({
        tone: "ok",
        text:
          r.earnedCommissionIds?.length > 0
            ? "Costs finalized — the commission on this order is now eligible."
            : "Costs finalized. The commission becomes eligible once the order is also paid in full and closed.",
      });
      refresh();
    },
    onError: (e) => setMsg({ tone: "risk", text: errReason(e) }),
  });

  const [reopenOpen, setReopenOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState("");
  const reopen = useMutation({
    mutationFn: () =>
      api(`/orders/${orderId}/reopen-costs`, { method: "POST", body: JSON.stringify({ reason: reopenReason.trim() }) }),
    onSuccess: () => {
      setMsg({ tone: "ok", text: "Costs reopened. Finalize them again once every bill is in." });
      setReopenOpen(false);
      setReopenReason("");
      refresh();
    },
    onError: (e) => setMsg({ tone: "risk", text: errReason(e) }),
  });

  if (q.isError) return <Notice tone="risk">{errReason(q.error)}</Notice>;
  if (!q.data) return <Loading />;
  const d = q.data;
  const o = d.order;
  const elig = d.eligibility;
  const reviewLine = d.commissions.find((c) => c.needs_review);
  const locked = d.commissions.some((c) => c.status === "paid" || c.payout_batch_id);

  return (
    <div>
      {msg && (
        <Notice tone={msg.tone} className="mb-5">
          {msg.text}
        </Notice>
      )}

      <div className="mb-6 flex flex-wrap gap-x-10 gap-y-3 rounded-md border border-rule bg-white p-5 text-[15px]">
        <Fact label="Client">{o.client_name ?? "—"}</Fact>
        <Fact label="Opportunity">
          <Link to={`/projects/${o.project_id}`} className="underline underline-offset-2">
            {o.opportunity ?? "Open"}
          </Link>
        </Fact>
        <Fact label="Order">#{o.order_number}</Fact>
        <Fact label="Invoice">{o.invoice_number ? `#${o.invoice_number}` : "Not invoiced yet"}</Fact>
        <Fact label="Client Rep">{nameOr(o.client_rep_name)}</Fact>
        <Fact label="Order Rep on the opportunity">{o.order_rep_name ?? "None"}</Fact>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Section title="Can it be paid?">
          <div className="rounded-md border border-rule bg-white p-5">
            {elig?.eligible ? (
              <p className="flex items-center gap-2 text-[16px] font-medium text-ok">
                <CheckCircle2 className="h-5 w-5" aria-hidden /> Eligible since {fmtDay(elig.eligibleAt)}
              </p>
            ) : (
              <>
                <p className="text-[15px] text-muted">
                  Commission becomes eligible when the order is paid in full, closed, and its costs are finalized. Still waiting on:
                </p>
                <ul className="mt-2 space-y-1.5">
                  {(elig?.reasonText ?? []).map((r) => (
                    <li key={r} className="flex items-center gap-2 text-[16px] text-warn">
                      <CircleAlert className="h-5 w-5" aria-hidden /> {r}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <div className="mt-4 border-t border-[#e7e7e4] pt-4">
              {o.costs_finalized_at ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-[15px] text-ink">
                    Costs finalized {fmtDay(o.costs_finalized_at)}
                    {o.costs_finalized_by_name ? ` by ${o.costs_finalized_by_name}` : ""}.
                  </p>
                  {!reopenOpen && (
                    <Button variant="outline" onClick={() => setReopenOpen(true)}>
                      Reopen costs
                    </Button>
                  )}
                </div>
              ) : (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-[15px] text-ink">
                    Costs are not finalized. Finalize once every purchase order is billed and every bill is approved.
                  </p>
                  <Button onClick={() => finalize.mutate()} disabled={finalize.isPending}>
                    {finalize.isPending ? "Finalizing…" : "Finalize costs"}
                  </Button>
                </div>
              )}
              {reopenOpen && (
                <div className="mt-4 space-y-3">
                  <Field label="Why are the costs being reopened?">
                    <textarea className={TEXTAREA} rows={2} value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <Button onClick={() => reopen.mutate()} disabled={reopenReason.trim().length < 2 || reopen.isPending}>
                      Reopen costs
                    </Button>
                    <Button variant="ghost" onClick={() => setReopenOpen(false)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Section>

        <Section title="Margins">
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-rule bg-rule">
            <Num label="Revenue" value={money(d.margins.revenue)} />
            <Num label="Costs billed" value={money(d.margins.billedCost)} />
            <Num label="Gross profit as booked" value={money(d.margins.bookedGrossProfit)} note="Used while estimated" />
            <Num label="Gross profit as billed" value={money(d.margins.billedGrossProfit)} note="Used once eligible" />
          </div>
        </Section>
      </div>

      {reviewLine && <ResolveReview orderId={orderId} code={reviewLine.needs_review!} onDone={(t) => { setMsg({ tone: "ok", text: t }); refresh(); }} />}

      <SplitEditor detail={d} locked={locked} onDone={(t) => { setMsg({ tone: "ok", text: t }); refresh(); }} />

      <Section title="Commission on this order">
        {d.commissions.length === 0 ? (
          <p className="rounded-md border border-rule bg-white p-5 text-[15px] text-muted">
            No commission yet — it's calculated when the final invoice is created.
          </p>
        ) : (
          <div className={T_WRAP}>
            <table className="w-full">
              <thead className={T_HEAD}>
                <tr>
                  <th className={T_TH}>Rep</th>
                  <th className={T_TH}>Plan · rate</th>
                  <th className={`${T_TH} text-right`}>Gross profit</th>
                  <th className={`${T_TH} text-right`}>Pool</th>
                  <th className={`${T_TH} text-right`}>Share</th>
                  <th className={`${T_TH} text-right`}>Commission</th>
                  <th className={T_TH}>Status</th>
                </tr>
              </thead>
              <tbody>
                {d.commissions.map((c) => (
                  <tr key={c.id} className={T_ROW}>
                    <td className={T_TD}>
                      {nameOr(c.rep_name)} <span className="text-muted">· {roleLabel(c.rep_role)}</span>
                    </td>
                    <td className={T_TD}>
                      {c.plan_name ?? "No plan"}
                      {c.plan_version ? ` v${c.plan_version}` : ""} · {ratePct(c.rate)}
                    </td>
                    <td className={T_NUM}>{money(c.gross_profit)}</td>
                    <td className={T_NUM}>{money(c.pool)}</td>
                    <td className={T_NUM}>{c.share_percent != null ? `${Number(c.share_percent)}%` : "—"}</td>
                    <td className={`${T_NUM} font-semibold`}>{money(c.amount)}</td>
                    <td className={T_TD}>
                      <Pill tone={lineStatusTone(c.status)}>{LINE_STATUS_LABEL[c.status] ?? c.status}</Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Adjustments">
        {d.adjustments.length === 0 ? (
          <p className="rounded-md border border-rule bg-white p-5 text-[15px] text-muted">None on this order.</p>
        ) : (
          <div className={T_WRAP}>
            <table className="w-full">
              <thead className={T_HEAD}>
                <tr>
                  <th className={T_TH}>Rep</th>
                  <th className={T_TH}>Why</th>
                  <th className={`${T_TH} text-right`}>Amount</th>
                  <th className={T_TH}>Status</th>
                  <th className={T_TH}>Recorded</th>
                </tr>
              </thead>
              <tbody>
                {d.adjustments.map((a) => (
                  <tr key={a.id} className={T_ROW}>
                    <td className={T_TD}>{nameOr(a.rep_name)}</td>
                    <td className={T_TD}>
                      <span className="block">{a.reason ?? "—"}</span>
                      <span className="text-[14px] text-muted">{ADJ_SOURCE_LABEL[a.source] ?? a.source}</span>
                    </td>
                    <td className={`${T_NUM} ${Number(a.amount) < 0 ? "text-risk" : ""}`}>{money(a.amount)}</td>
                    <td className={T_TD}>
                      <Pill tone={a.status === "approved" ? "active" : a.status === "rejected" ? "closed" : "warn"}>
                        {a.status === "pending" ? "Waiting for approval" : a.status === "approved" ? "Approved" : "Rejected"}
                      </Pill>
                    </td>
                    <td className={T_TD}>{fmtDay(a.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="History">
        {d.history.length === 0 ? (
          <p className="rounded-md border border-rule bg-white p-5 text-[15px] text-muted">Nothing recorded yet.</p>
        ) : (
          <ol className="divide-y divide-[#e7e7e4] rounded-md border border-rule bg-white">
            {d.history.map((h, i) => {
              const detail = eventDetail(h.event_type, h.payload);
              return (
                <li key={`${h.occurred_at}-${i}`} className="flex flex-wrap items-baseline justify-between gap-3 px-5 py-3">
                  <span className="text-[15px] text-ink">
                    <span className="font-medium">{eventLabel(h.event_type)}</span>
                    {detail ? <span className="text-muted"> — {detail}</span> : null}
                  </span>
                  <span className="text-[14px] text-muted">
                    {h.actor_name ?? "Flow (automatic)"} · {new Date(h.occurred_at).toLocaleString()}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </Section>
    </div>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[13px] text-muted">{label}</div>
      <div className="mt-0.5 font-medium text-ink">{children}</div>
    </div>
  );
}

function Num({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="bg-white p-4">
      <div className="text-[14px] text-muted">{label}</div>
      <div className="mt-1 text-[22px] font-medium tabular-nums text-ink">{value}</div>
      {note ? <div className="mt-0.5 text-[13px] text-muted">{note}</div> : null}
    </div>
  );
}

function SplitEditor({ detail, locked, onDone }: { detail: OrderDetail; locked: boolean; onDone: (t: string) => void }) {
  const status = usePlanStatus();
  const plans = usePlans();
  const a = detail.allocation;
  const clientRepId = a?.clientRepId ?? detail.order.client_rep_id;
  const [orderRepId, setOrderRepId] = useState<string>(a?.orderRepId ?? detail.order.order_rep_id ?? "");
  const [preset, setPreset] = useState<SplitPreset>(a?.preset ?? "100/0");
  const [note, setNote] = useState(a?.note ?? "");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setOrderRepId(a?.orderRepId ?? detail.order.order_rep_id ?? "");
    setPreset(a?.preset ?? "100/0");
    setNote(a?.note ?? "");
  }, [a?.orderRepId, a?.preset, a?.note, detail.order.order_rep_id]);

  const save = useMutation({
    mutationFn: () =>
      api(`/commissions/orders/${detail.order.id}/allocation`, {
        method: "PUT",
        body: JSON.stringify({
          order_rep_id: orderRepId || null,
          preset,
          // Keep Finance's earlier pool-plan decision when re-saving the split.
          pool_plan_id: a?.poolPlanId ?? null,
          note: note.trim() || null,
        }),
      }),
    onSuccess: () => {
      setErr(null);
      onDone(preset === "100/0" ? "Saved — the Client Rep keeps the whole commission." : `Split ${preset} approved.`);
    },
    onError: (e) => setErr(errReason(e)),
  });

  const labels = status.data?.splitPresets;
  const repName = (id: string | null | undefined) => plans.data?.reps.find((r) => r.id === id)?.full_name ?? "Unassigned";
  const needsOrderRep = preset !== "100/0" && !orderRepId;

  return (
    <Section
      title="Client Rep / Order Rep split"
      intro="Nothing splits until you approve a split here — assigning an Order Rep on the opportunity alone never splits commission. A split divides the one pool; it never adds a second commission."
    >
      <div className="rounded-md border border-rule bg-white p-5">
        <p className="mb-4 text-[15px] text-ink">
          {a ? (
            <>
              Current: <span className="font-semibold">{a.preset}</span>
              {a.orderRepId && a.preset !== "100/0" ? ` with ${repName(a.orderRepId)} as Order Rep` : " — Client Rep keeps 100%"}
              <span className="text-muted"> · approved {fmtDay(a.approvedAt)}</span>
              {a.note ? <span className="text-muted"> · “{a.note}”</span> : null}
            </>
          ) : (
            <>No split approved — the Client Rep ({nameOr(detail.order.client_rep_name)}) keeps 100%.</>
          )}
        </p>
        {locked ? (
          <Notice tone="info">
            This order's commission is already in a payout batch or paid, so the split can't change. Record any correction as an adjustment.
          </Notice>
        ) : (
          <div className="grid gap-5 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
            <Field label="Order Rep">
              <select className={INPUT} value={orderRepId} onChange={(e) => setOrderRepId(e.target.value)}>
                <option value="">No Order Rep</option>
                {(plans.data?.reps ?? [])
                  .filter((r) => r.id !== clientRepId)
                  .map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.full_name}
                      {r.current_plan ? ` (${r.current_plan})` : ""}
                    </option>
                  ))}
              </select>
            </Field>
            <fieldset>
              <legend className="mb-1.5 text-[14px] font-medium text-ink">Split (Client Rep / Order Rep)</legend>
              <div className="flex flex-col gap-2">
                {SPLIT_PRESET_ORDER.map((p) => (
                  <label key={p} className="flex cursor-pointer items-center gap-3 text-[15px] text-ink">
                    <input type="radio" name="split-preset" className="h-4 w-4" checked={preset === p} onChange={() => setPreset(p)} />
                    {labels?.[p] ?? p}
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="lg:col-span-2">
              <Field label="Note (optional)">
                <input className={INPUT} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why this split" />
              </Field>
            </div>
            <div className="flex flex-wrap items-center gap-3 lg:col-span-2">
              <Button onClick={() => save.mutate()} disabled={save.isPending || needsOrderRep}>
                {save.isPending ? "Saving…" : "Approve split"}
              </Button>
              {needsOrderRep && <span className="text-[14px] text-warn">Choose the Order Rep for a split.</span>}
              {err && <span className="text-[15px] text-risk">{err}</span>}
            </div>
          </div>
        )}
      </div>
    </Section>
  );
}

function ResolveReview({ orderId, code, onDone }: { orderId: string; code: string; onDone: (t: string) => void }) {
  const plans = usePlans();
  const [planId, setPlanId] = useState("");
  const [note, setNote] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const resolve = useMutation({
    mutationFn: () =>
      api(`/commissions/orders/${orderId}/resolve`, {
        method: "POST",
        body: JSON.stringify({ pool_plan_id: planId, note: note.trim() || null }),
      }),
    onSuccess: () => onDone("Plan chosen — the commission was recalculated on that plan."),
    onError: (e) => setErr(errReason(e)),
  });
  return (
    <div className="mb-8 rounded-md border-2 border-[#e2c58f] bg-warn-bg p-5">
      <h3 className="text-[18px] font-semibold text-warn">Needs review</h3>
      <p className="mt-1 text-[15px] text-warn">{reviewText(code)}</p>
      <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)_auto] lg:items-end">
        <Field label="Choose the plan that sets the pool">
          <select className={INPUT} value={planId} onChange={(e) => setPlanId(e.target.value)}>
            <option value="">Choose a plan…</option>
            {(plans.data?.plans ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Note (optional)">
          <input className={INPUT} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
        <Button onClick={() => resolve.mutate()} disabled={!planId || resolve.isPending}>
          Approve plan
        </Button>
      </div>
      {err && <p className="mt-3 text-[15px] text-risk">{err}</p>}
    </div>
  );
}
